import jsPDF from "jspdf";
import { LEGAL_TERMS_EN, LEGAL_TERMS_EL } from "./legal";
import { hasPhoto, fetchPhotoDataUrl } from "./privatePhotos";

/**
 * Generate a PDF from a Collab/Model submission.
 * - Includes the GP brand logo as the document banner
 * - Includes ALL 10 legal terms in BOTH English & Greek (collab kind)
 * - Embeds Noto Sans (TTF) for proper Greek character rendering
 * - For ModelApply: appends the photo pages with the candidate's submitted images
 */

// Cache loaded resources between PDFs in the same session
let _logoDataUrl = null;
let _notoSansBase64 = null;

async function getLogo() {
  if (_logoDataUrl) return _logoDataUrl;
  try {
    const r = await fetch("/gp-logo.png");
    if (!r.ok) return null;
    const blob = await r.blob();
    _logoDataUrl = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(blob);
    });
    return _logoDataUrl;
  } catch { return null; }
}

async function getNotoSansBase64() {
  if (_notoSansBase64) return _notoSansBase64;
  try {
    const r = await fetch("/NotoSans-Regular.ttf");
    if (!r.ok) return null;
    const buf = await r.arrayBuffer();
    // Convert ArrayBuffer to base64
    const bytes = new Uint8Array(buf);
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    _notoSansBase64 = btoa(binary);
    return _notoSansBase64;
  } catch { return null; }
}

export async function downloadSubmissionPDF(s, kind, returnBlob = false) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const margin = 40;
  let y = margin;

  // For COLLAB kind: build the list of ID photos directly from the submission
  // (uploaded via CollabForm). Fallback to legacy Stripe Identity fetch if not available.
  let idPhotos = [];
  if (kind === "collab" && s.id) {
    // Private uploads are fetched through the admin-only, audited content endpoint.
    if (hasPhoto(s, "collab", "id_front")) idPhotos.push({ kind: "front", slot: "id_front" });
    if (hasPhoto(s, "collab", "id_back")) idPhotos.push({ kind: "back", slot: "id_back" });
    if (hasPhoto(s, "collab", "selfie")) idPhotos.push({ kind: "selfie", slot: "selfie" });
    if (idPhotos.length === 0 && s.identity_verified) {
      // Legacy fallback — pull from Stripe Identity files
      try {
        const { api } = await import("./api");
        const r = await api.get(`/api/collab-submissions/${s.id}/id-photos`);
        idPhotos = r.data?.photos || [];
      } catch { /* render PDF without ID photos */ }
    }
  }

  // Embed Noto Sans for Greek + Latin support
  try {
    const fontB64 = await getNotoSansBase64();
    if (fontB64) {
      doc.addFileToVFS("NotoSans-Regular.ttf", fontB64);
      doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
      doc.setFont("NotoSans", "normal");
    }
  } catch { /* font fallback to helvetica */ }

  const ensure = (need) => { if (y + need > ph - 50) { doc.addPage(); y = margin; } };
  const heading = (text, size = 18, color = [0xff, 0x33, 0x00]) => {
    ensure(size + 10);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    doc.text(text, margin, y);
    y += size + 6;
    doc.setTextColor(20, 20, 20);
  };
  const line = (label, value) => {
    if (!value && value !== 0) return;
    ensure(20);
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    doc.text(String(label).toUpperCase(), margin, y);
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    const val = String(value);
    const lines = doc.splitTextToSize(val, pw - margin * 2 - 100);
    doc.text(lines, margin + 110, y);
    y += Math.max(14, lines.length * 14) + 4;
  };
  const sectionRule = () => {
    ensure(14);
    doc.setDrawColor(0xff, 0x33, 0x00);
    doc.setLineWidth(1);
    doc.line(margin, y, pw - margin, y);
    y += 10;
  };

  // ── Header banner with logo ─────────────────────────
  doc.setFillColor(10, 15, 26);
  doc.rect(0, 0, pw, 80, "F");
  const logo = await getLogo();
  if (logo) {
    // Render the logo at the left side of the banner. Aspect-ratio-aware fit.
    try {
      doc.addImage(logo, "PNG", margin, 10, 60, 60, undefined, "FAST");
    } catch { /* skip */ }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.text("GP · GIO PILOS", margin + 80, 38);
  doc.setFontSize(10);
  doc.setTextColor(20, 184, 166);
  doc.text(
    kind === "collab"
      ? "MODEL RELEASE & CONTENT COLLABORATION AGREEMENT"
      : "MODEL APPLICATION",
    margin + 80, 56
  );
  doc.setFontSize(8);
  doc.setTextColor(180, 180, 180);
  doc.text("giopilos.com · gio@giopilos.eu · Athens, Greece", margin + 80, 70);
  y = 110;

  // ── Personal section ────────────────────────────────
  if (kind === "collab") {
    heading(s.full_name || "Untitled");
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Submitted at: ${(s.submitted_at || "").slice(0, 19).replace("T", " ")} UTC`, margin, y);
    y += 18;
    sectionRule();

    heading("Personal", 14);
    line("Stage Name", s.full_name);
    line("Legal Name", s.legal_name);
    line("Email", s.email);
    line("Phone", s.phone);
    line("Date of Birth", s.date_of_birth);
    line("Address", s.address);
    line("Country", s.country);
    line("State", s.state);
    line("City", s.city);
    line("Pronouns", s.pronouns);
    y += 6;

    heading("Socials", 14);
    line("Twitter / X", s.twitter_handle ? `@${(s.twitter_handle || "").replace(/^@/, "")}` : "");
    line("Instagram", s.instagram_handle ? `@${(s.instagram_handle || "").replace(/^@/, "")}` : "");
    line("OnlyFans", s.onlyfans_link);
    line("JustForFans", s.justforfans_link);
    line("Other Links", s.other_links);
    y += 6;

    heading("Legal", 14);
    line("Agreed to all terms", s.agreed_terms ? "YES" : "NO");
    line("Signed As (typed)", s.signature_name);
    line("Signing Locale", s.locale ? s.locale.toUpperCase() : "");
    line("ID Photos uploaded", ["id_front", "id_back", "selfie"].every((k) => hasPhoto(s, "collab", k)) ? "YES (3 photos)" : "NO");
    line("Drawn signature", s.signature_drawn_data_url ? "YES" : "NO");
    if (s.identity_verified) line("Stripe Identity", `VERIFIED · ${s.identity_session_id || ""}`);
  } else {
    heading(s.name || "Untitled");
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Submitted at: ${(s.submitted_at || "").slice(0, 19).replace("T", " ")} UTC`, margin, y);
    y += 18;
    sectionRule();

    heading("Personal", 14);
    line("Name", s.name);
    line("Email", s.email);
    line("X / Twitter", s.twitter_handle ? `@${(s.twitter_handle || "").replace(/^@/, "")}` : "");
    line("Location", s.location);
    line("Body Type", s.body_type);
    line("Dick Size", s.dick_size);
    line("Languages", s.languages);
    y += 6;

    heading("OnlyFans / JFF", 14);
    line("OF Status", s.onlyfans_status);
    line("OF Link", s.onlyfans_link);
    line("JFF Status", s.justforfans_status);
    line("JFF Link", s.justforfans_link);
    y += 6;

    heading("Collaboration", 14);
    line("Role / Position", s.role_position);
    line("Content Types", s.content_types);
    line("Availability", s.availability);
    line("Kinks / Interests", s.interests_kinks);
    line("Boundaries / Limits", s.boundaries);
    line("Notes", s.additional_notes);
  }

  // Admin notes
  if (s.admin_notes) {
    y += 4;
    heading("Admin Notes", 14);
    doc.setFontSize(10);
    doc.setTextColor(20, 20, 20);
    const lines = doc.splitTextToSize(s.admin_notes, pw - margin * 2);
    doc.text(lines, margin, y);
    y += lines.length * 14 + 6;
  }

  // ── LEGAL TERMS — full bilingual rendering for COLLAB kind ──
  if (kind === "collab") {
    const renderTerms = async (terms, lang) => {
      doc.addPage();
      y = margin;
      heading(
        lang === "EN"
          ? "AGREEMENT TERMS — ENGLISH"
          : "ΟΡΟΙ ΣΥΜΦΩΝΙΑΣ — ΕΛΛΗΝΙΚΑ",
        16, [255, 51, 0]
      );
      sectionRule();
      terms.forEach((t, i) => {
        // Title
        ensure(36);
        doc.setFontSize(11);
        doc.setTextColor(20, 184, 166);
        doc.text(`${i + 1}. ${t.title}`, margin, y);
        y += 16;
        // Body
        doc.setFontSize(9.5);
        doc.setTextColor(35, 35, 35);
        const lines = doc.splitTextToSize(t.body, pw - margin * 2);
        // Re-paginate if body crosses
        for (const ln of lines) {
          ensure(13);
          doc.text(ln, margin, y);
          y += 12;
        }
        y += 8;
      });
      // Signature confirmation block
      ensure(80);
      y += 6;
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pw - margin, y);
      y += 14;
      doc.setFontSize(10);
      doc.setTextColor(35, 35, 35);
      const confirm =
        lang === "EN"
          ? `I, ${s.signature_name || "_______________"}, confirm that I have read, understood, and agree to all the terms above. I am 18+ and have full legal capacity to sign.`
          : `Εγώ, ο/η ${s.signature_name || "_______________"}, επιβεβαιώνω ότι έχω διαβάσει, κατανοήσει και αποδέχομαι όλους τους παραπάνω όρους. Είμαι 18+ και έχω πλήρη δικαιοπρακτική ικανότητα.`;
      const cl = doc.splitTextToSize(confirm, pw - margin * 2);
      cl.forEach((ln) => { ensure(13); doc.text(ln, margin, y); y += 12; });
      y += 14;
      // Drawn signature image (if available)
      if (s.signature_drawn_data_url) {
        ensure(90);
        doc.setFontSize(9);
        doc.setTextColor(110, 110, 110);
        doc.text(lang === "EN" ? "Drawn signature:" : "Υπογραφή (σχέδιο):", margin, y);
        y += 8;
        // Frame
        doc.setFillColor(255, 255, 255);
        doc.rect(margin, y, 240, 70, "F");
        doc.setDrawColor(180, 180, 180);
        doc.rect(margin, y, 240, 70);
        try {
          // Convert white-on-transparent → black-on-white for print
          const inverted = await invertSignatureForPDF(s.signature_drawn_data_url);
          const finalUrl = inverted || s.signature_drawn_data_url;
          // eslint-disable-next-line no-console
          console.log("[PDF] sig data url len:", (s.signature_drawn_data_url || "").length, "inverted:", !!inverted);
          doc.addImage(finalUrl, "PNG", margin + 4, y + 4, 232, 62, undefined, "FAST");
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[PDF] signature embed failed:", err);
          // Fallback: write a diagnostic note inside the frame
          doc.setFontSize(8);
          doc.setTextColor(180, 0, 0);
          doc.text("(signature could not be embedded — raw data length: "
            + (s.signature_drawn_data_url ? s.signature_drawn_data_url.length : 0) + " chars)",
            margin + 6, y + 36);
        }
        y += 80;
      } else if (kind === "collab") {
        // Diagnostic when no drawn signature was saved for a COLLAB submission
        ensure(40);
        doc.setFontSize(9);
        doc.setTextColor(160, 80, 0);
        doc.text(lang === "EN"
          ? "⚠ No drawn signature was captured for this submission."
          : "⚠ Δεν καταγράφηκε σχεδιασμένη υπογραφή για αυτή την υποβολή.",
          margin, y);
        y += 16;
      }
      // Typed signature
      if (s.signature_name) {
        doc.setFontSize(10);
        doc.setTextColor(20, 20, 20);
        doc.text(`x ${s.signature_name}`, margin, y);
        y += 14;
      }
      doc.setFontSize(9);
      doc.setTextColor(110, 110, 110);
      doc.text(
        lang === "EN"
          ? `Signed at: ${(s.submitted_at || "").slice(0, 19).replace("T", " ")} UTC${s.identity_verified ? " · Stripe Identity ✅" : ""}`
          : `Υπογράφηκε: ${(s.submitted_at || "").slice(0, 19).replace("T", " ")} UTC${s.identity_verified ? " · Stripe Identity ✅" : ""}`,
        margin, y
      );
    };
    await renderTerms(LEGAL_TERMS_EN, "EN");
    await renderTerms(LEGAL_TERMS_EL, "EL");

    // ── ID PHOTOS (Stripe Identity verified) ───────────
    if (idPhotos.length > 0) {
      const labelFor = (k) => k === "front" ? "ID DOCUMENT · FRONT"
        : k === "back" ? "ID DOCUMENT · BACK"
        : k === "selfie" ? "SELFIE WITH ID" : k.toUpperCase();
      for (const p of idPhotos) {
        try {
          doc.addPage();
          y = margin;
          heading(labelFor(p.kind), 14);
          doc.setFontSize(9);
          doc.setTextColor(140, 140, 140);
          doc.text(p.slot ? "Private upload — fetched via audited admin access at PDF generation time" : "Verified via Stripe Identity — image fetched at PDF generation time", margin, y);
          y += 16;
          const dataUrl = p.slot
            ? await fetchPhotoDataUrl("collab", s.id, p.slot, s).catch(() => null)
            : await imageToDataUrl(p.url);
          if (dataUrl) {
            // Detect format from data url for jsPDF (PNG or JPEG)
            const fmt = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
            doc.addImage(dataUrl, fmt, margin, y, 480, 600, undefined, "FAST");
          } else {
            doc.setFontSize(11);
            doc.setTextColor(255, 51, 0);
            doc.text("⚠ Image fetch failed — see Stripe dashboard.", margin, y + 10);
          }
        } catch { /* skip */ }
      }
    }
  }

  // ── ModelApply photo pages ─────────────────────────
  if (kind === "modelapply") {
    const photos = ["face_photo", "front_nude_photo", "back_nude_photo", "dick_photo", "ass_photo"]
      .filter(k => hasPhoto(s, "modelapply", k))
      .map(k => ({ key: k }));
    for (const p of photos) {
      try {
        doc.addPage();
        y = margin;
        heading(p.key.replace(/_/g, " ").toUpperCase(), 14);
        const dataUrl = await fetchPhotoDataUrl("modelapply", s.id, p.key, s).catch(() => null);
        if (dataUrl) {
          doc.addImage(dataUrl, "JPEG", margin, y, 400, 500, undefined, "FAST");
        }
      } catch { /* skip */ }
    }
  }

  // Footer disclaimer on every page
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      `Confidential — generated by GP App · giopilos.com · ${new Date().toISOString().slice(0, 10)} · Page ${i} / ${total}`,
      margin, ph - 20
    );
  }

  const fname = `${kind}-${(s.full_name || s.name || "submission").replace(/[^a-z0-9]+/gi, "_")}-${(s.id || "").slice(0, 8)}.pdf`;
  
  // If returnBlob is true, return the blob instead of downloading
  if (returnBlob) {
    return doc.output('blob');
  }
  
  doc.save(fname);
}

async function imageToDataUrl(src) {
  if (!src) return null;
  if (src.startsWith("data:")) return src;
  try {
    const r = await fetch(src, { mode: "cors" });
    const blob = await r.blob();
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Convert the dark-canvas drawn signature (transparent bg + white ink) into
// a white-bg + black-ink PNG suitable for PDF embedding.
// Strategy: draw the signature on a TRANSPARENT canvas first, walk the pixels,
// and for any pixel with alpha>0 turn it solid BLACK. Leave alpha=0 pixels as
// transparent → composite over white at the end.
async function invertSignatureForPDF(dataUrl) {
  if (!dataUrl) return null;
  return await new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth || 600;
        c.height = img.naturalHeight || 200;
        const ctx = c.getContext("2d");
        // Draw signature on TRANSPARENT canvas (no white bg fill yet)
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, c.width, c.height);
        const px = data.data;
        // Any opaque (ink) pixel → black. Transparent pixels stay transparent.
        for (let i = 0; i < px.length; i += 4) {
          if (px[i + 3] > 10) {
            px[i] = 0; px[i + 1] = 0; px[i + 2] = 0; px[i + 3] = 255;
          }
        }
        ctx.putImageData(data, 0, 0);
        // Composite onto white background so the final PNG is white+black
        const out = document.createElement("canvas");
        out.width = c.width;
        out.height = c.height;
        const octx = out.getContext("2d");
        octx.fillStyle = "#ffffff";
        octx.fillRect(0, 0, out.width, out.height);
        octx.drawImage(c, 0, 0);
        resolve(out.toDataURL("image/png"));
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    } catch { resolve(null); }
  });
}
